import React from 'react';

export const Skeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`lms-skeleton ${className}`} aria-hidden="true" />
);

const SkeletonPage: React.FC<{ label: string; children: React.ReactNode; className?: string }> = ({
  label,
  children,
  className = '',
}) => (
  <div role="status" aria-busy="true" aria-label={label} className={className}>
    <span className="sr-only">{label}…</span>
    {children}
  </div>
);

const PanelShell: React.FC<{ children?: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`lms-panel ${className}`}>{children}</div>
);

const PanelHeaderSkeleton: React.FC<{ title?: string; action?: string }> = ({
  title = 'w-28',
  action = 'w-14',
}) => (
  <div className="flex items-center justify-between gap-4 border-b border-rule px-5 py-4">
    <Skeleton className={`h-4 rounded-md ${title}`} />
    <Skeleton className={`h-3 rounded-md ${action}`} />
  </div>
);

export const HeaderSkeleton: React.FC<{ actions?: boolean }> = ({ actions = true }) => (
  <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
    <div className="min-w-0 space-y-3">
      <Skeleton className="h-3 w-40 rounded-md" />
      <Skeleton className="h-9 w-[min(340px,78vw)] rounded-xl" />
      <Skeleton className="h-4 w-[min(540px,90vw)] rounded-md" />
    </div>
    {actions && (
      <div className="flex flex-wrap gap-2">
        <Skeleton className="h-10 w-36 rounded-lg" />
        <Skeleton className="h-10 w-32 rounded-lg" />
      </div>
    )}
  </div>
);

const ProgrammeCardSkeleton: React.FC = () => (
  <div className="rounded-2xl border border-rule bg-white p-4 shadow-[0_10px_30px_-28px_rgba(15,40,90,0.35)]">
    <div className="flex gap-4">
      <Skeleton className="h-[86px] w-[92px] flex-none rounded-xl" />
      <div className="min-w-0 flex-1 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <Skeleton className="h-5 w-24 rounded-full" />
          <Skeleton className="h-4 w-4 rounded-md" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-4 w-11/12 rounded-md" />
          <Skeleton className="h-4 w-3/5 rounded-md" />
        </div>
        <Skeleton className="h-3 w-2/3 rounded-md" />
        <div className="flex items-center gap-2.5">
          <Skeleton className="h-2 flex-1 rounded-full" />
          <Skeleton className="h-3 w-8 rounded-md" />
        </div>
      </div>
    </div>
  </div>
);

const GlanceSkeleton: React.FC = () => (
  <PanelShell>
    <PanelHeaderSkeleton title="w-24" action="w-12" />
    <div className="grid grid-cols-3 divide-x divide-rule px-1 py-4 sm:px-2">
      {[0, 1, 2].map((i) => (
        <div key={i} className="min-w-0 space-y-3 px-1.5 text-center sm:px-3">
          <Skeleton className="mx-auto h-4 w-4 rounded-full" />
          <Skeleton className="mx-auto h-6 w-10 rounded-md" />
          <Skeleton className="mx-auto h-2.5 w-16 rounded-md" />
        </div>
      ))}
    </div>
  </PanelShell>
);

const NoticesSkeleton: React.FC = () => (
  <PanelShell>
    <PanelHeaderSkeleton title="w-20" action="w-16" />
    <div className="divide-y divide-rule">
      {[0, 1, 2].map((i) => (
        <div key={i} className="space-y-2 px-5 py-3.5">
          <Skeleton className="h-3.5 w-4/5 rounded-md" />
          <Skeleton className="h-3 w-1/2 rounded-md" />
        </div>
      ))}
    </div>
  </PanelShell>
);

const DeskSkeleton: React.FC = () => (
  <PanelShell>
    <PanelHeaderSkeleton title="w-24" action="w-10" />
    <div className="space-y-1 p-2">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center gap-3 rounded-xl px-3 py-2.5">
          <Skeleton className="h-4 w-4 flex-none rounded-md" />
          <Skeleton className="h-3.5 flex-1 rounded-md" />
          <Skeleton className="h-3 w-3 flex-none rounded-md" />
        </div>
      ))}
    </div>
  </PanelShell>
);

const RecommendationCardSkeleton: React.FC = () => (
  <div className="flex overflow-hidden rounded-2xl border border-rule bg-white">
    <Skeleton className="hidden w-[184px] flex-none rounded-none sm:block" />
    <div className="flex min-w-0 flex-1 flex-col space-y-3 p-4">
      <div className="flex items-center justify-between gap-3">
        <Skeleton className="h-3 w-24 rounded-md" />
        <Skeleton className="h-3 w-16 rounded-md" />
      </div>
      <Skeleton className="h-4 w-4/5 rounded-md" />
      <Skeleton className="h-3 w-full rounded-md" />
      <div className="flex items-center justify-between border-t border-rule pt-3">
        <Skeleton className="h-3 w-24 rounded-md" />
        <Skeleton className="h-4 w-4 rounded-md" />
      </div>
    </div>
  </div>
);

export const DashboardSkeleton: React.FC = () => (
  <SkeletonPage label="Loading your overview" className="space-y-6">
    <HeaderSkeleton />

    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
      <div className="min-w-0 space-y-6">
        <div className="relative overflow-hidden rounded-3xl bg-blue-100/60 p-6 sm:p-8">
          <Skeleton className="h-5 w-32 rounded-full bg-white/60" />
          <Skeleton className="mt-5 h-9 w-[min(460px,88%)] rounded-xl bg-white/70" />
          <Skeleton className="mt-2 h-9 w-[min(320px,62%)] rounded-xl bg-white/55" />
          <Skeleton className="mt-5 h-4 w-[min(370px,80%)] rounded-md bg-white/50" />
          <Skeleton className="mt-7 h-2.5 w-full max-w-[440px] rounded-full bg-white/45" />
          <div className="mt-6 flex flex-wrap gap-2">
            <Skeleton className="h-10 w-32 rounded-lg bg-white/75" />
            <Skeleton className="h-10 w-36 rounded-lg bg-white/50" />
          </div>
          <div className="absolute right-8 top-1/2 hidden h-[150px] w-[150px] -translate-y-1/2 rounded-full border-[10px] border-white/25 lg:block" />
        </div>

        <section>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="space-y-2">
              <Skeleton className="h-5 w-40 rounded-md" />
              <Skeleton className="h-3.5 w-48 rounded-md" />
            </div>
            <Skeleton className="h-4 w-28 rounded-md" />
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            {[0, 1, 2].map((i) => (
              <ProgrammeCardSkeleton key={i} />
            ))}
          </div>
        </section>
      </div>

      <aside className="space-y-5">
        <GlanceSkeleton />
        <NoticesSkeleton />
        <DeskSkeleton />
      </aside>
    </div>

    <section className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_300px]">
      <div className="min-w-0">
        <div className="space-y-2">
          <Skeleton className="h-5 w-40 rounded-md" />
          <Skeleton className="h-3.5 w-52 rounded-md" />
        </div>
        <div className="mt-4 space-y-3">
          <RecommendationCardSkeleton />
          <RecommendationCardSkeleton />
        </div>
      </div>
      <div className="min-h-[280px] rounded-2xl bg-blue-100/60 p-6">
        <Skeleton className="h-10 w-10 rounded-xl bg-white/70" />
        <Skeleton className="mt-5 h-3 w-28 rounded-md bg-white/60" />
        <Skeleton className="mt-3 h-6 w-4/5 rounded-md bg-white/65" />
        <Skeleton className="mt-2 h-6 w-3/5 rounded-md bg-white/55" />
        <Skeleton className="mt-4 h-3 w-full rounded-md bg-white/45" />
        <Skeleton className="mt-2 h-3 w-5/6 rounded-md bg-white/45" />
        <Skeleton className="mt-5 h-10 w-40 rounded-lg bg-white/70" />
      </div>
    </section>
  </SkeletonPage>
);

export const LearnSkeleton: React.FC = () => (
  <SkeletonPage label="Opening the classroom" className="space-y-5">
    <Skeleton className="h-4 w-32 rounded-md" />

    <div className="relative overflow-hidden rounded-2xl bg-blue-100/60 p-6">
      <div className="flex flex-wrap items-center gap-2.5">
        <Skeleton className="h-5 w-24 rounded-full bg-white/65" />
        <Skeleton className="h-3 w-20 rounded-md bg-white/55" />
      </div>
      <Skeleton className="mt-4 h-9 w-[min(520px,86%)] rounded-xl bg-white/70" />
      <Skeleton className="mt-3 h-4 w-[min(460px,78%)] rounded-md bg-white/50" />
      <div className="mt-5 flex flex-wrap gap-4">
        <Skeleton className="h-3 w-24 rounded-md bg-white/50" />
        <Skeleton className="h-3 w-24 rounded-md bg-white/50" />
      </div>
    </div>

    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
      <div className="min-w-0 space-y-5">
        <div className="aspect-video rounded-2xl bg-blue-950/15" />
        <PanelShell>
          <PanelHeaderSkeleton title="w-44" action="w-20" />
          <div className="space-y-3 p-5">
            <Skeleton className="h-4 w-4/5 rounded-md" />
            <Skeleton className="h-3.5 w-full rounded-md" />
            <Skeleton className="h-3.5 w-11/12 rounded-md" />
            <Skeleton className="h-3.5 w-2/3 rounded-md" />
            <div className="flex flex-wrap gap-2 pt-2">
              <Skeleton className="h-9 w-36 rounded-lg" />
              <Skeleton className="h-9 w-28 rounded-lg" />
            </div>
          </div>
        </PanelShell>
        <div className="flex flex-wrap gap-2">
          <Skeleton className="h-9 w-24 rounded-lg" />
          <Skeleton className="h-9 w-28 rounded-lg" />
          <Skeleton className="h-9 w-32 rounded-lg" />
        </div>
        <PanelShell>
          <div className="space-y-3 p-5">
            <Skeleton className="h-4 w-36 rounded-md" />
            <Skeleton className="h-3.5 w-full rounded-md" />
            <Skeleton className="h-3.5 w-5/6 rounded-md" />
            <Skeleton className="h-24 w-full rounded-xl" />
          </div>
        </PanelShell>
      </div>

      <aside>
        <PanelShell>
          <PanelHeaderSkeleton title="w-28" action="w-12" />
          <div className="space-y-1 p-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-3 rounded-xl px-2.5 py-3">
                <Skeleton className="h-6 w-6 flex-none rounded-full" />
                <div className="min-w-0 flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-4/5 rounded-md" />
                  <Skeleton className="h-2.5 w-1/2 rounded-md" />
                </div>
              </div>
            ))}
          </div>
        </PanelShell>
      </aside>
    </div>
  </SkeletonPage>
);

export const CertificatesSkeleton: React.FC = () => (
  <SkeletonPage label="Loading your certificates">
    <HeaderSkeleton />
    <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
      <PanelShell className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-rule px-5 py-4">
          <div className="space-y-2">
            <Skeleton className="h-3 w-24 rounded-md" />
            <Skeleton className="h-5 w-44 rounded-md" />
          </div>
          <Skeleton className="h-5 w-20 rounded-full" />
        </div>
        <div className="grid gap-8 px-6 py-8 sm:grid-cols-[minmax(0,1fr)_180px] sm:items-center">
          <div className="space-y-4">
            <Skeleton className="h-3 w-28 rounded-md" />
            <Skeleton className="h-8 w-4/5 rounded-lg" />
            <Skeleton className="h-4 w-3/5 rounded-md" />
            <Skeleton className="h-3 w-full rounded-md" />
            <Skeleton className="h-3 w-5/6 rounded-md" />
            <div className="grid grid-cols-2 gap-4 pt-3">
              <div className="space-y-2">
                <Skeleton className="h-2.5 w-20 rounded-md" />
                <Skeleton className="h-3.5 w-28 rounded-md" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-2.5 w-20 rounded-md" />
                <Skeleton className="h-3.5 w-28 rounded-md" />
              </div>
            </div>
          </div>
          <Skeleton className="mx-auto h-40 w-40 rounded-full" />
        </div>
        <div className="flex flex-wrap gap-2 border-t border-rule px-5 py-4">
          <Skeleton className="h-9 w-32 rounded-lg" />
          <Skeleton className="h-9 w-28 rounded-lg" />
        </div>
      </PanelShell>
      <aside className="space-y-6">
        <PanelShell>
          <PanelHeaderSkeleton title="w-28" action="w-16" />
          <div className="space-y-3 p-5">
            <Skeleton className="h-3.5 w-full rounded-md" />
            <Skeleton className="h-3.5 w-5/6 rounded-md" />
            <Skeleton className="h-3.5 w-2/3 rounded-md" />
          </div>
        </PanelShell>
        <PanelShell>
          <PanelHeaderSkeleton title="w-24" action="w-10" />
          <div className="space-y-3 p-5">
            <Skeleton className="h-10 w-full rounded-lg" />
            <Skeleton className="h-10 w-full rounded-lg" />
          </div>
        </PanelShell>
      </aside>
    </div>
  </SkeletonPage>
);

export const AccountSkeleton: React.FC = () => (
  <SkeletonPage label="Loading your student account">
    <HeaderSkeleton />
    <div className="mt-7 flex flex-wrap items-start gap-4 border-y border-rule py-4">
      <Skeleton className="h-12 w-12 flex-none rounded-2xl" />
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Skeleton className="h-5 w-40 rounded-md" />
          <Skeleton className="h-5 w-20 rounded-full" />
          <Skeleton className="h-5 w-24 rounded-full" />
        </div>
        <Skeleton className="h-3.5 w-64 max-w-full rounded-md" />
      </div>
      <Skeleton className="h-3 w-24 rounded-md" />
    </div>

    <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
      <div className="space-y-6">
        <PanelShell>
          <PanelHeaderSkeleton title="w-20" action="w-24" />
          <div className="space-y-4 px-5 py-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Skeleton className="h-2.5 w-16 rounded-md" />
                <Skeleton className="h-10 w-full rounded-lg" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-2.5 w-16 rounded-md" />
                <Skeleton className="h-10 w-full rounded-lg" />
              </div>
            </div>
            <div className="space-y-2">
              <Skeleton className="h-2.5 w-16 rounded-md" />
              <Skeleton className="h-10 w-full rounded-lg" />
            </div>
            <div className="flex items-center justify-between border-t border-rule pt-4">
              <Skeleton className="h-3 w-44 rounded-md" />
              <Skeleton className="h-9 w-28 rounded-lg" />
            </div>
          </div>
        </PanelShell>
        <PanelShell>
          <PanelHeaderSkeleton title="w-24" action="w-20" />
          <div className="space-y-4 px-5 py-5">
            <Skeleton className="h-3.5 w-4/5 rounded-md" />
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Skeleton className="h-2.5 w-24 rounded-md" />
                <Skeleton className="h-10 w-full rounded-lg" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-2.5 w-24 rounded-md" />
                <Skeleton className="h-10 w-full rounded-lg" />
              </div>
            </div>
            <div className="flex items-center justify-between border-t border-rule pt-4">
              <Skeleton className="h-3 w-32 rounded-md" />
              <Skeleton className="h-9 w-28 rounded-lg" />
            </div>
          </div>
        </PanelShell>
      </div>

      <aside className="space-y-6">
        <PanelShell>
          <PanelHeaderSkeleton title="w-20" action="w-10" />
          <div className="divide-y divide-rule">
            {[0, 1].map((i) => (
              <div key={i} className="space-y-2 px-5 py-4">
                <Skeleton className="h-3 w-28 rounded-md" />
                <Skeleton className="h-6 w-12 rounded-md" />
                <Skeleton className="h-2.5 w-36 rounded-md" />
              </div>
            ))}
          </div>
        </PanelShell>
        <PanelShell>
          <PanelHeaderSkeleton title="w-32" action="w-8" />
          <div className="space-y-1 p-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-3">
                <Skeleton className="h-2.5 w-5 rounded-md" />
                <Skeleton className="h-3.5 flex-1 rounded-md" />
                <Skeleton className="h-3 w-3 rounded-md" />
              </div>
            ))}
          </div>
        </PanelShell>
      </aside>
    </div>
  </SkeletonPage>
);

export const AdminDashboardSkeleton: React.FC = () => (
  <SkeletonPage label="Loading the operations desk">
    <HeaderSkeleton />
    <div className="mt-7 overflow-x-auto pb-1">
      <div className="flex w-max gap-1 rounded-2xl border border-rule bg-white/85 p-1.5">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-9 w-20 rounded-xl" />
        ))}
      </div>
    </div>

    <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start">
      <div className="space-y-6">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="rounded-2xl border border-rule bg-white px-5 py-5">
              <Skeleton className="h-2.5 w-20 rounded-md" />
              <Skeleton className="mt-3 h-7 w-16 rounded-md" />
              <Skeleton className="mt-2 h-3 w-28 rounded-md" />
            </div>
          ))}
        </div>
        <PanelShell>
          <PanelHeaderSkeleton title="w-20" action="w-16" />
          <div className="divide-y divide-rule">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center justify-between gap-4 px-5 py-4">
                <div className="min-w-0 flex-1 space-y-2">
                  <Skeleton className="h-3.5 w-2/5 rounded-md" />
                  <Skeleton className="h-3 w-3/5 rounded-md" />
                </div>
                <Skeleton className="h-5 w-10 rounded-md" />
              </div>
            ))}
          </div>
        </PanelShell>
      </div>
      <aside className="space-y-6">
        <PanelShell>
          <PanelHeaderSkeleton title="w-24" action="w-8" />
          <div className="divide-y divide-rule">
            {[0, 1, 2].map((i) => (
              <div key={i} className="space-y-2 px-5 py-4">
                <Skeleton className="h-3.5 w-24 rounded-md" />
                <Skeleton className="h-3 w-full rounded-md" />
              </div>
            ))}
          </div>
        </PanelShell>
        <PanelShell>
          <PanelHeaderSkeleton title="w-20" action="w-8" />
          <div className="divide-y divide-rule">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="space-y-2 px-5 py-3">
                <Skeleton className="h-3.5 w-24 rounded-md" />
                <Skeleton className="h-3 w-4/5 rounded-md" />
              </div>
            ))}
          </div>
        </PanelShell>
      </aside>
    </div>
  </SkeletonPage>
);

export const CourseEditorSkeleton: React.FC = () => (
  <SkeletonPage label="Loading the programme editor" className="space-y-5">
    <Skeleton className="h-4 w-32 rounded-md" />
    <div className="space-y-3">
      <Skeleton className="h-8 w-64 max-w-full rounded-xl" />
      <Skeleton className="h-3.5 w-80 max-w-full rounded-md" />
    </div>
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_380px] xl:items-start">
      <div className="space-y-6">
        <PanelShell>
          <PanelHeaderSkeleton title="w-32" action="w-20" />
          <div className="space-y-4 px-5 py-5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-2.5 w-20 rounded-md" />
                <Skeleton className="h-10 w-full rounded-lg" />
              </div>
            ))}
            <div className="grid gap-4 sm:grid-cols-2">
              <Skeleton className="h-10 w-full rounded-lg" />
              <Skeleton className="h-10 w-full rounded-lg" />
            </div>
            <Skeleton className="h-24 w-full rounded-xl" />
            <div className="flex justify-end border-t border-rule pt-4">
              <Skeleton className="h-9 w-32 rounded-lg" />
            </div>
          </div>
        </PanelShell>
        <PanelShell>
          <PanelHeaderSkeleton title="w-28" action="w-20" />
          <div className="space-y-3 p-5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center justify-between gap-3 rounded-xl border border-rule px-3 py-3">
                <Skeleton className="h-3.5 w-2/5 rounded-md" />
                <Skeleton className="h-3 w-16 rounded-md" />
              </div>
            ))}
          </div>
        </PanelShell>
      </div>
      <aside className="space-y-6">
        <PanelShell>
          <PanelHeaderSkeleton title="w-32" action="w-16" />
          <div className="space-y-4 p-5">
            <Skeleton className="h-20 w-full rounded-xl" />
            <Skeleton className="h-10 w-full rounded-lg" />
            <Skeleton className="h-10 w-full rounded-lg" />
          </div>
        </PanelShell>
      </aside>
    </div>
  </SkeletonPage>
);

export const GradingQueueSkeleton: React.FC = () => (
  <SkeletonPage label="Loading the grading queue" className="mt-6 space-y-4">
    <div className="flex flex-wrap items-center justify-between gap-3 border-y border-rule py-3">
      <Skeleton className="h-10 w-48 rounded-xl" />
      <Skeleton className="h-3 w-24 rounded-md" />
    </div>
    {[0, 1].map((i) => (
      <PanelShell key={i}>
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-rule px-5 py-4">
          <div className="space-y-2">
            <Skeleton className="h-4 w-48 rounded-md" />
            <Skeleton className="h-3 w-32 rounded-md" />
          </div>
          <Skeleton className="h-5 w-20 rounded-full" />
        </div>
        <div className="grid gap-4 px-5 py-5 lg:grid-cols-[minmax(0,1fr)_280px]">
          <div className="space-y-3">
            <Skeleton className="h-3.5 w-full rounded-md" />
            <Skeleton className="h-3.5 w-5/6 rounded-md" />
            <Skeleton className="h-3.5 w-2/3 rounded-md" />
          </div>
          <div className="space-y-3">
            <Skeleton className="h-10 w-full rounded-lg" />
            <Skeleton className="h-20 w-full rounded-xl" />
            <div className="flex gap-2">
              <Skeleton className="h-9 w-24 rounded-lg" />
              <Skeleton className="h-9 w-24 rounded-lg" />
            </div>
          </div>
        </div>
      </PanelShell>
    ))}
  </SkeletonPage>
);

export const CourseDetailSkeleton: React.FC = () => (
  <SkeletonPage label="Loading the programme" className="space-y-5">
    <Skeleton className="h-4 w-32 rounded-md" />
    <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
      <div className="min-w-0 space-y-5">
        <div className="flex flex-wrap gap-2">
          <Skeleton className="h-5 w-20 rounded-full" />
          <Skeleton className="h-5 w-16 rounded-full" />
          <Skeleton className="h-3 w-24 rounded-md" />
        </div>
        <Skeleton className="h-10 w-4/5 rounded-xl" />
        <Skeleton className="h-4 w-full rounded-md" />
        <Skeleton className="h-4 w-5/6 rounded-md" />
        <Skeleton className="h-3.5 w-2/3 rounded-md" />
        <Skeleton className="mt-4 h-48 w-full rounded-2xl" />
        <div className="space-y-3">
          <Skeleton className="h-5 w-32 rounded-md" />
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-3 rounded-xl border border-rule px-4 py-3">
              <Skeleton className="h-6 w-6 flex-none rounded-full" />
              <Skeleton className="h-3.5 flex-1 rounded-md" />
            </div>
          ))}
        </div>
      </div>
      <aside>
        <PanelShell>
          <div className="space-y-4 p-5">
            <Skeleton className="h-3 w-24 rounded-md" />
            <Skeleton className="h-7 w-32 rounded-md" />
            <Skeleton className="h-10 w-full rounded-lg" />
            <Skeleton className="h-3 w-full rounded-md" />
            <Skeleton className="h-3 w-4/5 rounded-md" />
            <Skeleton className="h-10 w-full rounded-lg" />
          </div>
        </PanelShell>
      </aside>
    </div>
  </SkeletonPage>
);

export const ListRowsSkeleton: React.FC<{ rows?: number; label?: string }> = ({
  rows = 3,
  label = 'Loading records',
}) => (
  <div role="status" aria-busy="true" aria-label={label} className="divide-y divide-rule">
    <span className="sr-only">{label}…</span>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Skeleton className="h-3.5 w-2/5 rounded-md" />
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
          <Skeleton className="h-3 w-3/5 rounded-md" />
        </div>
        <Skeleton className="h-8 w-16 rounded-lg" />
      </div>
    ))}
  </div>
);

export const ListPanelSkeleton: React.FC<{ rows?: number; label?: string }> = ({
  rows = 3,
  label = 'Loading records',
}) => (
  <SkeletonPage label={label}>
    <PanelShell>
      <PanelHeaderSkeleton title="w-32" action="w-16" />
      <div className="divide-y divide-rule">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center justify-between gap-4 px-5 py-4">
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-3.5 w-2/5 rounded-md" />
              <Skeleton className="h-3 w-3/5 rounded-md" />
            </div>
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
        ))}
      </div>
    </PanelShell>
  </SkeletonPage>
);

export const GenericPageSkeleton: React.FC = () => (
  <SkeletonPage label="Loading" className="space-y-6">
    <HeaderSkeleton />
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
      <PanelShell className="min-h-[320px]" />
      <PanelShell className="min-h-[220px]" />
    </div>
  </SkeletonPage>
);
